import React, {
  RefObject,
  useEffect,
  useState,
  MouseEvent,
} from 'react';
import useRecordedAudioVisualizer from 'views/EMR/components/Snap/visualizer/useRecordedAudioVisualizer';

type AudioPlayerWithVisualizerProps = {
  recordedBlob: Blob;
  audioRef?: RefObject<HTMLAudioElement | null>;
  onPlayingChange: (playing: boolean) => void;
  onTimeUpdate?: (currentTime: number) => void;
};

export default function AudioPlayerWithVisualizer({
  recordedBlob,
  audioRef,
  onPlayingChange,
  onTimeUpdate,
}: AudioPlayerWithVisualizerProps) {
  const [audioUrl, setAudioUrl] = useState<string>('');
  const {
    containerRef,
    analyzeAudioBlob,
    startProgressAnimation,
    stopProgressAnimation,
    resetProgress,
    handleCanvasClick,
  } = useRecordedAudioVisualizer();

  // Create object URL and analyze audio when component mounts
  useEffect(() => {
    const url = URL.createObjectURL(recordedBlob);
    setAudioUrl(url);

    // Analyze the audio blob to generate waveform
    analyzeAudioBlob(recordedBlob);

    // Cleanup function to revoke object URL
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [recordedBlob, analyzeAudioBlob]);

  const handlePlay = () => {
    onPlayingChange(true);
    if (audioRef?.current) {
      startProgressAnimation(audioRef.current);
    }
  };

  const handleCanvasClickWithPlay = (e: MouseEvent<HTMLCanvasElement>) => {
    if (audioRef?.current) {
      handleCanvasClick(e, audioRef.current, handlePlay);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef?.current && onTimeUpdate) {
      onTimeUpdate(audioRef.current.currentTime);
    }
  };

  const handlePause = () => {
    onPlayingChange(false);
    stopProgressAnimation();
  };

  const handleEnded = () => {
    onPlayingChange(false);
    resetProgress();
  };

  return (
    <div className="relative flex items-center">
      <canvas
        ref={containerRef}
        width={346}
        height={56}
        style={{ borderRadius: '4px', cursor: 'pointer' }}
        onClick={handleCanvasClickWithPlay}
      />
      <audio
        ref={audioRef as RefObject<HTMLAudioElement>}
        src={audioUrl}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate}
        onLoadedData={handleTimeUpdate}
        onCanPlay={handleTimeUpdate}
        onError={(e) => console.error('[Snap] Audio error:', e)}
        preload="auto"
      >
        <track kind="captions" srcLang="en" label="English captions" />
      </audio>
    </div>
  );
}
